<template>
  <div
    v-tooltip="{
      content: translate('endDateIsBeforeStartDate'),
      placement: 'top',
      onShow: () => isDueDateBeforeStartDate && !isMobile,
    }"
    class="calendar-card-item"
    :class="{
      'left-edge-cropped': isLeftEdgeCropped,
      'right-edge-cropped': isRightEdgeCropped,
      'due-before-start': isDueDateBeforeStartDate,
    }"
    :style="cardStyle"
    @click="handleCardClick"
  >
    <em
      v-tooltip="{
        content: statusTooltipText,
        placement: 'top-start',
        onShow: () => !!statusTooltipText && !isMobile,
      }"
      class="custom-icons calendar-card-status"
      :style="cardStatusStyle"
    />
    <div class="card-item-title">{{ card.title }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from 'vuex';
import secondsToMilliseconds from 'date-fns/secondsToMilliseconds';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import useOpenCard from '@/utilities/composables/useOpenCard';
import useTranslate from '@/utilities/composables/useTranslate';
import { getCardStatus } from '@/utilities/defaultCardStatuses';
import { ResourceType } from '@/types/resources';
import cardStatusType from '@/types/cardStatusType';

const store = useStore();
const { iconPath, getCustomIconStyle } = useCustomIcon();
const { openCard } = useOpenCard();
const { translate } = useTranslate();

const props = defineProps({
  card: {
    type: Object,
    required: true,
  },
  // weekId - timestamp of the week
  weekId: {
    type: Number,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  maxCardsVisible: {
    type: Number,
    required: true,
  },
  inTooltip: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['open-card']);

const card = computed(() => props.card.calendarTask);
const croppedEdges = computed(() => props.card.croppedEdges);
const style = computed(() => props.card.style);

const cardStartDate = computed(() => secondsToMilliseconds(card.value.start_date));
const cardEndDate = computed(() => secondsToMilliseconds(card.value.due_date));

const areCardDatesSet = computed(() => !!(cardStartDate.value && cardEndDate.value));

const isRightEdgeCropped = computed(() => {
  return croppedEdges.value.has('right');
});

const isLeftEdgeCropped = computed(() => {
  return croppedEdges.value.has('left');
});

const isDueDateBeforeStartDate = computed(() => {
  return cardEndDate.value < cardStartDate.value && areCardDatesSet.value;
});

const cardStyle = computed(() => ({
  ...style.value,
}));

const statusIcon = computed(() => {
  const isEpic = card.value.type === ResourceType.Epic;
  return (
    getCardStatus(card.value.status, isEpic)?.icon ||
    getCardStatus(cardStatusType.Backlog, isEpic).icon
  );
});

const lists = computed(() => store.getters.lists);

const statusColor = computed(() => {
  return (
    lists.value[card.value.list_id]?.color ||
    card.value.list_color ||
    getCardStatus(card.value.status)?.color ||
    getCardStatus(cardStatusType.Backlog).color
  );
});

const cardStatusStyle = computed(() => ({
  ...getCustomIconStyle(iconPath(statusIcon.value)),
  backgroundColor: statusColor.value,
}));

const cardList = computed(() => {
  return store.getters.lists[card.value.list_id] || {};
});

const statusTooltipText = computed(() => card.value.list_title || cardList.value.title || '');

const handleCardClick = () => {
  emit('open-card');
  openCard(card.value);
};
</script>

<style lang="scss">
$edge-crop-size: 10px;
.calendar-card-item {
  @apply flex items-center gap-1 pl-[3px] pr-[7px] py-0 mx-[7px] border border-solid border-emphasised-border rounded bg-emphasised-bg relative cursor-pointer box-border;
  height: $calendarCardHeight;

  &:hover {
    @apply bg-emphasised-hover;
  }

  &.left-edge-cropped {
    @apply ml-0 border-l-0 rounded-l-none;
    mask-image: linear-gradient(to right, transparent, black $edge-crop-size);
  }

  &.right-edge-cropped {
    @apply mr-0 border-r-0 rounded-r-none;
    mask-image: linear-gradient(to left, transparent, black $edge-crop-size);
  }

  &.due-before-start {
    border-color: $warning400;
  }

  // if there is both left and right edge cropping, then remove the border
  &.left-edge-cropped.right-edge-cropped {
    mask-image: linear-gradient(
      to right,
      transparent,
      black $edge-crop-size,
      black calc(100% - $edge-crop-size),
      transparent
    );
  }

  .calendar-card-status {
    @apply m-1 flex-none;
  }

  .card-item-title {
    @apply text-sm truncate text-text-default;
  }
}
</style>
